/**
 * Estado de filtros da aba Clientes do Dashboard.
 */

export const EMPTY_CLIENTS_FILTER_STATE = {
  ids_grupos: [],
  ufs: [],
  cidades: [],
  tags: [],
  ids_potenciais: [],
  date_field: "cadastro",
  all_period: false,
  labels: {
    groups: {},
    ufs: {},
    cities: {},
    tags: {},
    potentials: {},
  },
};

const POTENTIAL_OPTIONS = [
  { id: 1, label: "Baixo" },
  { id: 2, label: "Médio" },
  { id: 3, label: "Alto" },
];

const DATE_FIELD_OPTIONS = [
  { id: "cadastro", labelKey: "dateFieldCadastro", fallback: "Data de cadastro" },
  { id: "alteracao", labelKey: "dateFieldAlteracao", fallback: "Última alteração" },
  { id: "aniversario", labelKey: "dateFieldAniversario", fallback: "Aniversário" },
];

export function getClientsPotentialOptions() {
  return POTENTIAL_OPTIONS;
}

export function getClientsDateFieldOptions() {
  return DATE_FIELD_OPTIONS;
}

function toPositiveIntArray(raw) {
  const list = Array.isArray(raw) ? raw : raw != null && raw !== "" ? String(raw).split(",") : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const n = Number(String(item ?? "").trim());
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

function toStringIdArray(raw) {
  const list = Array.isArray(raw) ? raw : raw != null && raw !== "" ? String(raw).split(",") : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const s = String(item ?? "").trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function normalizeLabels(map) {
  if (!map || typeof map !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(map)) {
    const id = String(k).trim();
    const label = String(v ?? "").trim();
    if (!id || !label) continue;
    out[id] = label;
  }
  return out;
}

function resolveDateField(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (v === "alteracao" || v === "atualizacao") return "alteracao";
  if (v === "aniversario" || v === "aniversariante") return "aniversario";
  return "cadastro";
}

export function normalizeClientsFilterState(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  const labels = s.labels && typeof s.labels === "object" ? s.labels : {};
  return {
    ids_grupos: toPositiveIntArray(s.ids_grupos),
    ufs: toStringIdArray(s.ufs),
    cidades: toStringIdArray(s.cidades),
    tags: toStringIdArray(s.tags),
    ids_potenciais: toPositiveIntArray(s.ids_potenciais),
    date_field: resolveDateField(s.date_field),
    all_period: Boolean(s.all_period),
    labels: {
      groups: normalizeLabels(labels.groups),
      ufs: normalizeLabels(labels.ufs),
      cities: normalizeLabels(labels.cities),
      tags: normalizeLabels(labels.tags),
      potentials: normalizeLabels(labels.potentials),
    },
  };
}

export function countClientsFilterCriteria(state) {
  const s = normalizeClientsFilterState(state);
  let n = 0;
  if (s.ids_grupos.length) n += 1;
  if (s.ufs.length) n += 1;
  if (s.cidades.length) n += 1;
  if (s.tags.length) n += 1;
  if (s.ids_potenciais.length) n += 1;
  if (s.date_field && s.date_field !== "cadastro") n += 1;
  if (s.all_period) n += 1;
  return n;
}

export function clientsFilterStateToQueryParams(state) {
  const s = normalizeClientsFilterState(state);
  const params = {};
  if (s.ids_grupos.length) params.ids_grupos = s.ids_grupos;
  if (s.ufs.length) params.ufs = s.ufs;
  if (s.cidades.length) params.cidades = s.cidades;
  if (s.tags.length) params.tags = s.tags;
  if (s.ids_potenciais.length) params.ids_potenciais = s.ids_potenciais;
  if (s.date_field && s.date_field !== "cadastro") params.date_field = s.date_field;
  if (s.all_period) params.all_period = 1;
  return params;
}

export function buildClientsFilterSummaries(state, t) {
  const s = normalizeClientsFilterState(state);
  const items = [];
  const tr = (key, fallback, vars) =>
    t(`dashboardPage.clientsFilters.${key}`, { defaultValue: fallback, ...vars });

  s.ids_grupos.forEach((id) => {
    const label = s.labels.groups[String(id)] || `#${id}`;
    items.push({
      key: `group:${id}`,
      label,
      title: tr("chipGroupTooltip", "Grupo: {{value}}", { value: label }),
      tone: "emphasis",
    });
  });

  s.ufs.forEach((uf) => {
    const label = s.labels.ufs[uf] || uf;
    items.push({
      key: `uf:${uf}`,
      label,
      title: tr("chipUfTooltip", "UF: {{value}}", { value: label }),
      tone: "emphasis",
    });
  });

  s.cidades.forEach((cidade) => {
    const label = s.labels.cities[cidade] || cidade;
    items.push({
      key: `cidade:${cidade}`,
      label,
      title: tr("chipCityTooltip", "Cidade: {{value}}", { value: label }),
    });
  });

  s.tags.forEach((tag) => {
    const label =
      tag === "notag" || tag === "__notag__"
        ? tr("noTag", "Sem tag")
        : s.labels.tags[tag] || tag;
    items.push({
      key: `tag:${tag}`,
      label,
      title: tr("chipTagTooltip", "Tag: {{value}}", { value: label }),
    });
  });

  s.ids_potenciais.forEach((id) => {
    const fallback = POTENTIAL_OPTIONS.find((o) => o.id === id)?.label || `#${id}`;
    const label = s.labels.potentials[String(id)] || fallback;
    items.push({
      key: `potencial:${id}`,
      label,
      title: tr("chipPotentialTooltip", "Potencial: {{value}}", { value: label }),
    });
  });

  if (s.date_field && s.date_field !== "cadastro") {
    const opt = DATE_FIELD_OPTIONS.find((o) => o.id === s.date_field);
    const label = opt ? tr(opt.labelKey, opt.fallback) : s.date_field;
    items.push({
      key: "date_field",
      label,
      title: tr("chipDateFieldTooltip", "Campo de data: {{value}}", { value: label }),
    });
  }

  if (s.all_period) {
    items.push({
      key: "all_period",
      label: tr("allPeriod", "Todo o histórico"),
      title: tr("allPeriodTooltip", "Período da barra ignorado"),
      tone: "emphasis",
    });
  }

  return items;
}
